/**
 * Spectator screen controller.
 * Connects to an existing battle room and receives BATTLE_STATE messages from
 * the host or guest. Spectators do not send actions or participate in the battle.
 *
 * Note: Trystero's default signaling uses public DHT. For spectator support to work
 * in practice, the host/guest must be connected to the same room and the spectator
 * must also join that room. The current architecture sends a single peer message; a
 * production implementation would need multi-peer broadcast or a dedicated relay.
 */

import type { ScreenId } from "./home";
import type { BattleState, TrainerProfile } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, toast } from "../components";
import { pokemonBackUrl, pokemonFrontUrl, backgroundImageUrl } from "../../data/images";
import { resolveSpriteUrl } from "../../data/sprites";
import { Connection, type ConnectionState } from "../../p2p/connection";
import { ManualConnection } from "../../p2p/manual";
import type { IP2PConnection } from "../../p2p/base";
import { SyncManager } from "../../p2p/sync";
import { loadTrainerProfile } from "./trainer";
import { audioManager } from "../../data/audio";

export function renderSpectate(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);

  const header = el("header", { className: "app-header" });
  header.appendChild(el("h1", { text: "Spectate Battle" }));
  header.appendChild(button("Back to Home", () => navigate("home"), { variant: "secondary" }));
  container.appendChild(header);

  const main = el("main", { className: "app-main lobby-main" });

  let conn: IP2PConnection | null = null;
  let sync: SyncManager | null = null;
  let connectionState: ConnectionState = "disconnected";
  let currentConfig = { level: 50, turnTimerSeconds: 60, battleBackground: "grass" };
  let profiles: { p1: TrainerProfile; p2: TrainerProfile } | null = null;

  const statusEl = el("div", { className: "lobby-status" });
  function updateStatus(): void {
    clearChildren(statusEl);
    const stateText: Record<ConnectionState, string> = {
      disconnected: "Not connected",
      connecting: "Connecting...",
      connected: "Connected to battle room",
      reconnecting: "Reconnecting...",
      error: "Connection error",
    };
    statusEl.appendChild(el("p", { text: stateText[connectionState], className: `lobby-status-text lobby-status-${connectionState}` }));
    if (profiles) {
      statusEl.appendChild(el("p", { text: `${profiles.p1.name} vs ${profiles.p2.name}`, className: "lobby-info" }));
    }
  }

  // Room code input
  const joinSection = el("section", { className: "lobby-section" });
  joinSection.appendChild(el("h2", { text: "Enter Room Code" }));
  const codeInput = el("input", {
    className: "text-input room-code-input",
    attrs: { type: "text", placeholder: "ABC123", maxlength: "6" },
  }) as HTMLInputElement;
  codeInput.style.textTransform = "uppercase";
  joinSection.appendChild(codeInput);
  const joinBtn = button("Connect", async () => {
    const code = codeInput.value.trim().toUpperCase();
    if (code.length !== 6) { toast("Room code must be 6 characters", "error"); return; }
    joinBtn.disabled = true;
    joinBtn.textContent = "Connecting...";
    await startSpectatorConnection(code);
  });
  joinSection.appendChild(joinBtn);
  main.appendChild(joinSection);

  // Manual fallback
  const manualSection = el("section", { className: "lobby-section" });
  manualSection.appendChild(el("h2", { text: "Manual Connection Fallback" }));
  manualSection.appendChild(el("p", { text: "If Trystero DHT is blocked, paste an offer/answer from a player." }));
  const offerInput = el("textarea", { className: "manual-sdp-text", attrs: { placeholder: "Paste offer/answer here..." } }) as HTMLTextAreaElement;
  manualSection.appendChild(offerInput);
  const connectManualBtn = button("Connect", async () => {
    const text = offerInput.value.trim();
    if (!text) { toast("Paste an offer/answer first", "error"); return; }
    await startManualSpectatorConnection(text);
  });
  manualSection.appendChild(connectManualBtn);
  main.appendChild(manualSection);

  main.appendChild(statusEl);
  container.appendChild(main);

  // Spectator battle view container
  const battleView = el("div", { className: "battle-root" });
  battleView.style.display = "none";
  container.appendChild(battleView);

  async function startSpectatorConnection(code: string): Promise<void> {
    const profile = loadTrainerProfile();
    conn = new Connection({
      onStateChange: (state) => {
        connectionState = state;
        updateStatus();
      },
      onPeerJoin: () => {
        toast("Connected to battle room", "success");
        void sync?.sendHello(profile);
      },
      onPeerLeave: () => updateStatus(),
      onMessage: (msg) => sync?.handleMessage(msg),
    });

    sync = newSyncManager();
    try {
      await (conn as Connection).join(code);
    } catch {
      toast("Failed to connect", "error");
      joinBtn.disabled = false;
      joinBtn.textContent = "Connect";
    }
  }

  async function startManualSpectatorConnection(offerOrAnswer: string): Promise<void> {
    const profile = loadTrainerProfile();
    const manualConn = new ManualConnection(
      {
        onStateChange: (state) => {
          connectionState = state;
          updateStatus();
        },
        onPeerJoin: () => {
          toast("Connected manually to battle room", "success");
          void sync?.sendHello(profile);
        },
        onPeerLeave: () => updateStatus(),
        onMessage: (msg) => sync?.handleMessage(msg),
      },
      {
        onIceCandidate: (candidate) => {
          navigator.clipboard.writeText(candidate).then(() => toast("ICE candidate copied", "info")).catch(() => {});
        },
      },
    );
    conn = manualConn;
    sync = newSyncManager();
    try {
      const data = JSON.parse(offerOrAnswer) as RTCSessionDescriptionInit;
      if (data.type === "offer") {
        const answer = await manualConn.acceptOffer(offerOrAnswer);
        offerInput.value = answer;
        navigator.clipboard.writeText(answer).then(() => toast("Answer copied!", "success")).catch(() => {});
      } else if (data.type === "answer") {
        await manualConn.acceptAnswer(offerOrAnswer);
      } else {
        toast("Invalid SDP type", "error");
      }
    } catch {
      toast("Invalid offer/answer", "error");
    }
  }

  function newSyncManager(): SyncManager {
    return new SyncManager(conn!, false, {
      onStateChange: updateStatus,
      onPeerHello: (peerProfile) => {
        toast(`Connected to ${peerProfile.name}`, "info");
      },
      onPeerTeamHash: () => {},
      onConfigReceived: (cfg) => { currentConfig = cfg; },
      onBattleStart: () => {},
      onGuestTeam: () => {},
      onPeerReady: () => {},
      onBothReady: () => {},
      onActionReceived: () => {},
      onTurnReady: () => {},
      onPeerDisconnect: () => toast("Disconnected from battle room", "warning"),
      onPeerReconnect: () => toast("Reconnected to battle room", "success"),
      onChat: (text) => {
        void audioManager.playEffect({ type: "ui", sound: "chat" });
        toast(`Chat: ${text}`, "info");
      },
      onBattleState: (state, p) => {
        profiles = p;
        updateStatus();
        renderSpectatorBattleState(state, p, currentConfig);
      },
      onError: (msg) => toast(msg, "error"),
    });
  }

  function renderSpectatorBattleState(state: BattleState, profiles: { p1: TrainerProfile; p2: TrainerProfile }, config: { battleBackground: string }): void {
    clearChildren(battleView);
    battleView.style.display = "flex";
    battleView.style.backgroundImage = "";
    void resolveSpriteUrl(backgroundImageUrl(config.battleBackground)).then((url) => {
      if (url) battleView.style.backgroundImage = `url(${url})`;
    });

    const p1Active = state.p1[state.activeP1] ?? state.p1.find((p) => p.active);
    const p2Active = state.p2[state.activeP2] ?? state.p2.find((p) => p.active);

    const topArea = el("div", { className: "battle-area battle-foe-area" });
    topArea.appendChild(el("div", { className: "battle-trainer-info", text: profiles.p2.name }));
    if (p2Active) {
      const img = el("img", { className: "battle-sprite battle-foe-sprite", attrs: { alt: p2Active.species } }) as HTMLImageElement;
      img.style.imageRendering = "pixelated";
      void resolveSpriteUrl(pokemonFrontUrl(p2Active.species, p2Active.shiny)).then((url) => { if (url) img.src = url; });
      topArea.appendChild(img);
      topArea.appendChild(createHpBar(p2Active).container);
    }
    battleView.appendChild(topArea);

    const turnInfo = el("div", { className: "battle-turn-info" });
    turnInfo.appendChild(el("span", { text: `Turn ${state.turn}` }));
    if (state.weather) turnInfo.appendChild(el("span", { text: `Weather: ${state.weather}` }));
    battleView.appendChild(turnInfo);

    const bottomArea = el("div", { className: "battle-area battle-my-area" });
    bottomArea.appendChild(el("div", { className: "battle-trainer-info", text: profiles.p1.name }));
    if (p1Active) {
      const img = el("img", { className: "battle-sprite battle-my-sprite", attrs: { alt: p1Active.species } }) as HTMLImageElement;
      img.style.imageRendering = "pixelated";
      void resolveSpriteUrl(pokemonBackUrl(p1Active.species, p1Active.shiny)).then((url) => { if (url) img.src = url; });
      bottomArea.appendChild(img);
      bottomArea.appendChild(createHpBar(p1Active).container);
    }
    battleView.appendChild(bottomArea);

    if (state.ended && state.winner) {
      const result = el("div", { className: `battle-result ${state.winner === "p1" ? "win" : "lose"}`, text: state.winner === "p1" ? `${profiles.p1.name} Wins!` : `${profiles.p2.name} Wins!` });
      battleView.appendChild(result);
    }
  }

  function createHpBar(pokemon: { nickname: string; species: string; level: number; hp: number; maxHp: number }): { container: HTMLElement } {
    const container = el("div", { className: "hp-bar-container" });
    const label = el("span", { className: "hp-bar-label", text: `${pokemon.nickname || pokemon.species} Lv${pokemon.level}` });
    const barBg = el("div", { className: "hp-bar-bg" });
    const bar = el("div", { className: "hp-bar-fill" });
    const pct = Math.max(0, (pokemon.hp / pokemon.maxHp) * 100);
    bar.style.width = `${pct}%`;
    bar.className = `hp-bar-fill ${pct > 50 ? "hp-green" : pct > 25 ? "hp-yellow" : "hp-red"}`;
    barBg.appendChild(bar);
    container.appendChild(label);
    container.appendChild(barBg);
    return { container };
  }
}
